import { supabase } from '@/lib/supabase'
import type { DailyRate } from '@/types/database.types'
import { captureError } from '@/lib/sentry'

let _rateCache: { data: DailyRate; timestamp: number } | null = null
const CACHE_TTL = 2 * 60 * 1000 // 2 dakika

export const rateService = {
  /**
   * Bugünün kurlarını getirir.
   * Eğer henüz cron çalışmadıysa, en son kaydedilen kuru getirir (Fallback).
   */
  async getTodayRates(): Promise<DailyRate | null> {
    const today = new Date().toISOString().split('T')[0]
    return this.getRateByDate(today)
  },

  /**
   * Stale-While-Revalidate Kur Getirme Stratejisi
   * 1. Memory cache varsa anında döner.
   * 2. Yoksa DB'den son kuru çeker (hızlı, ~50ms).
   * 3. Arka planda Edge Function ile canlı kuru günceller.
   * 4. onUpdate callback ile güncel veri geldiğinde UI'ı bilgilendirir.
   */
  async getLiveRates(options?: {
    forceRefresh?: boolean
    onUpdate?: (freshRates: DailyRate) => void
  }): Promise<DailyRate | null> {
    // 1. Memory cache kontrolü (2dk TTL)
    if (!options?.forceRefresh && _rateCache && Date.now() - _rateCache.timestamp < CACHE_TTL) {
      return _rateCache.data
    }

    // 2. DB'den son kuru hızlıca çek (blocking — ama çok hızlı, ~50ms)
    const { data: dbRate } = await supabase
      .from('daily_rates')
      .select('*')
      .order('date', { ascending: false })
      .limit(1)
      .single()

    if (dbRate) {
      _rateCache = { data: dbRate, timestamp: Date.now() }
    }

    // 3. Arka planda Edge Function ile canlı veriyi güncelle (non-blocking)
    this._refreshInBackground(options?.forceRefresh ?? false, options?.onUpdate)

    // 4. DB verisini anında döndür
    return dbRate
  },

  /**
   * Bugün ve dünün kurlarını tek bir DB sorgusuyla çeker (Dashboard optimizasyonu).
   */
  async getLatestTwoRates(): Promise<{ today: DailyRate | null; yesterday: DailyRate | null }> {
    const { data: dbRates } = await supabase
      .from('daily_rates')
      .select('*')
      .order('date', { ascending: false })
      .limit(2)

    const today = dbRates && dbRates.length > 0 ? (dbRates[0] as DailyRate) : null
    const yesterday = dbRates && dbRates.length > 1 ? (dbRates[1] as DailyRate) : null

    if (today) {
      _rateCache = { data: today, timestamp: Date.now() }
    }

    this._refreshInBackground(false, undefined)

    return { today, yesterday }
  },

  /**
   * Edge Function ile canlı kuru arka planda çeker ve cache'i günceller.
   * Bloklamaz — fire-and-forget. Üstel geri çekilme (exponential backoff) içerir.
   */
  _refreshInBackground(forceRefresh: boolean, onUpdate?: (rates: DailyRate) => void, retryCount = 0) {
    const fetchPromise = supabase.functions.invoke('fetch-daily-rates', {
      body: { forceRefresh, source: 'frontend' },
    })

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Edge Function timeout (5s)')), 5000)
    )

    Promise.race([fetchPromise, timeoutPromise])
      .then((result: any) => {
        const { data: fetchResult, error: fetchError } = result
        if (fetchError) throw fetchError

        if (fetchResult?.success && fetchResult?.rates) {
          const freshRate = fetchResult.rates as DailyRate
          _rateCache = { data: freshRate, timestamp: Date.now() }

          // UI'a callback ile bildir (varsa)
          if (onUpdate) {
            onUpdate(freshRate)
          }
        }
      })
      .catch((err) => {
        if (retryCount < 2) {
          const delay = Math.pow(2, retryCount) * 2000
          setTimeout(() => this._refreshInBackground(forceRefresh, onUpdate, retryCount + 1), delay)
          return
        }
      })
  },

  /**
   * Belirli bir tarihteki kur verisini getirir.
   */
  async getRateByDate(date: string): Promise<DailyRate | null> {
    const today = new Date().toISOString().split('T')[0]

    // Eğer istenen tarih bugünse, canlı veriyi dene
    if (date === today) {
      return this.getLiveRates()
    }

    // Geçmiş tarih ise önce DB
    const { data } = await supabase.from('daily_rates').select('*').eq('date', date).maybeSingle()

    if (data) return data

    // DB'de yoksa function tetikle
    try {
      const { data: fetchResult } = await supabase.functions.invoke('fetch-daily-rates', {
        body: { date, source: 'frontend' },
      })
      if (fetchResult?.success) {
        const { data: newData } = await supabase
          .from('daily_rates')
          .select('*')
          .eq('date', date)
          .maybeSingle()
        return newData
      }
    } catch (err) {
      captureError(err, { service: 'rateService', action: 'getRateByDate', date })
    }

    return null
  },

  /**
   * Verilen tutarı ve para birimini, o günkü kura göre TRY'ye çevirir.
   */
  calculateTryValue(amount: number, currency: string, rates: DailyRate): number {
    switch (currency) {
      case 'TRY':
        return amount
      case 'USD':
        return amount * rates.usd_try
      case 'EUR':
        return amount * rates.eur_try
      case 'GRAM_GOLD':
      case 'GRAM_24_GOLD':
        return amount * rates.gram_24_try
      case 'GRAM_22_GOLD':
        return amount * rates.gram_22_try
      case 'QUARTER_GOLD':
        return amount * (rates.quarter_gold_try || rates.gram_22_try * 1.754)
      case 'HALF_GOLD':
        return amount * (rates.half_gold_try || rates.gram_22_try * 3.508)
      case 'FULL_GOLD':
        return amount * (rates.full_gold_try || rates.gram_22_try * 7.016)
      case 'GRAM_SILVER':
        return amount * (rates.gram_silver_try || 0)
      default:
        return amount
    }
  },
}
